import type * as GeoJSON from 'geojson'

/** Great-circle distance in metres. */
export function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000
  const rad = (d: number) => (d * Math.PI) / 180
  const a = Math.sin(rad(lat2 - lat1) / 2) ** 2
    + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lng2 - lng1) / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/** A Google Maps directions link to a stop. A plain external link: no share/copy (noExport.guard). */
export function directionsUrl(lat: number, lng: number, travelMode: 'walking' | 'driving'): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=${travelMode}`
}

const METERS_PER_MILE = 1609.344
const FEET_PER_METER = 3.28084

/** One unit system (US): feet under 0.1 mi (rounded to 10), else miles to one decimal. */
export function formatDistance(meters: number | null): string {
  if (meters == null) return ''
  if (meters < METERS_PER_MILE / 10) return `${Math.max(10, Math.round((meters * FEET_PER_METER) / 10) * 10)} ft`
  return `${(meters / METERS_PER_MILE).toFixed(1)} mi`
}

export function formatDuration(seconds: number | null): string {
  if (seconds == null) return ''
  const mins = Math.max(1, Math.round(seconds / 60))
  if (mins < 60) return `${mins} min`
  return `${Math.floor(mins / 60)}h ${mins % 60}m`
}

/** [lng, lat] tuple (GeoJSON / MapLibre order — note haversineMeters takes lat first). */
export type LngLat = [number, number]

const EARTH_R = 6371000
const toRad = (d: number) => (d * Math.PI) / 180
const toDeg = (r: number) => (r * 180) / Math.PI

/** Initial bearing, degrees clockwise from north, 0..360. */
export function bearingDegrees(from: LngLat, to: LngLat): number {
  const φ1 = toRad(from[1])
  const φ2 = toRad(to[1])
  const dλ = toRad(to[0] - from[0])
  const y = Math.sin(dλ) * Math.cos(φ2)
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ)
  return (toDeg(Math.atan2(y, x)) + 360) % 360
}

/** Move a point by east/north metres (local flat-earth approximation). */
export function offsetMeters(p: LngLat, eastM: number, northM: number): LngLat {
  const dLat = toDeg(northM / EARTH_R)
  const dLng = toDeg(eastM / (EARTH_R * Math.cos(toRad(p[1]))))
  return [p[0] + dLng, p[1] + dLat]
}

const segMeters = (a: LngLat, b: LngLat) => haversineMeters(a[1], a[0], b[1], b[0])

export function lineLengthMeters(line: LngLat[]): number {
  let total = 0
  for (let i = 1; i < line.length; i++) total += segMeters(line[i - 1], line[i])
  return total
}

/** Point `meters` along the line; clamps at the end (`done` true when meters >= length). */
export function pointAlongLine(
  line: LngLat[],
  meters: number,
): { point: LngLat; heading: number; done: boolean } {
  if (line.length === 0) return { point: [0, 0], heading: 0, done: true }
  if (line.length === 1) return { point: line[0], heading: 0, done: true }
  let remaining = Math.max(0, meters)
  let lastHeading = 0
  for (let i = 1; i < line.length; i++) {
    const a = line[i - 1]
    const b = line[i]
    const len = segMeters(a, b)
    const heading = len > 0 ? bearingDegrees(a, b) : lastHeading
    lastHeading = heading
    if (remaining <= len && len > 0) {
      if (remaining >= len && i === line.length - 1) return { point: b, heading, done: true }
      const t = remaining / len
      return { point: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t], heading, done: false }
    }
    remaining -= len
  }
  return { point: line[line.length - 1], heading: lastHeading, done: true }
}

/** Minimum distance from p to any segment (equirectangular projection per segment). */
export function distanceToLineMeters(p: LngLat, line: LngLat[]): number {
  if (line.length === 0) return Infinity
  if (line.length === 1) return segMeters(p, line[0])
  const kx = Math.cos(toRad(p[1])) * (Math.PI / 180) * EARTH_R
  const ky = (Math.PI / 180) * EARTH_R
  let best = Infinity
  for (let i = 1; i < line.length; i++) {
    const ax = (line[i - 1][0] - p[0]) * kx
    const ay = (line[i - 1][1] - p[1]) * ky
    const bx = (line[i][0] - p[0]) * kx
    const by = (line[i][1] - p[1]) * ky
    const dx = bx - ax
    const dy = by - ay
    const l2 = dx * dx + dy * dy
    const t = l2 === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / l2))
    best = Math.min(best, Math.hypot(ax + t * dx, ay + t * dy))
  }
  return best
}

/** A server route / next-segment GeoJSON string as a LineString feature; null when absent or unparsable. */
export function parseLine(json: string | null | undefined): GeoJSON.Feature<GeoJSON.LineString> | null {
  if (!json) return null
  try { return JSON.parse(json) as GeoJSON.Feature<GeoJSON.LineString> } catch { return null }
}
